import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Alert,
  Chip,
  Stack,
  Divider,
  Switch,
  FormGroup,
  Grid,
} from '@mui/material';
import FormSelect from '../components/common/FormSelect';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

const CreateQuestion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [structure, setStructure] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    area: '',
    topic: '',
    competency: '',
    component: '',
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    explanation: '',
    difficulty: '',
    // Nuevos campos
    hasContextText: false,
    contextText: '',
    hasContextImage: false,
    contextImage: '',
    hasQuestionImage: false,
    questionImage: '',
    isPartOfSet: false,
    questionSetId: '',
    questionOrder: 1,
  });

  useEffect(() => {
    fetchStructure();
  }, []);

  const fetchStructure = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/structure`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStructure(response.data);
    } catch (err) {
      console.error('Error fetching structure:', err);
    }
  };

  const handleAreaChange = (e) => {
    const area = e.target.value;
    setFormData({
      ...formData,
      area,
      topic: '',
      competency: '',
      component: '',
    });
  };

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({
      ...formData,
      options: newOptions,
    });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, ''],
    });
  };

  const removeOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      options: newOptions,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      // Validar datos
      if (formData.options.length < 2) {
        throw new Error('Debe haber al menos 2 opciones');
      }
      if (!formData.correct_answer) {
        throw new Error('Debe seleccionar la respuesta correcta');
      }
      if (!formData.options.includes(formData.correct_answer)) {
        throw new Error('La respuesta correcta debe ser una de las opciones');
      }

      const questionData = {
        question: formData.question,
        options: formData.options.filter(opt => opt.trim() !== ''),
        correct_answer: formData.correct_answer,
        area: formData.area,
        topic: formData.topic || null,
        competency: formData.competency || null,
        component: formData.component || null,
        explanation: formData.explanation,
        difficulty: formData.difficulty,
        context_text: formData.hasContextText ? formData.contextText : null,
        context_image: formData.hasContextImage ? formData.contextImage : null,
        question_image: formData.hasQuestionImage ? formData.questionImage : null,
        question_set_id: formData.isPartOfSet ? formData.questionSetId : null,
        question_order: formData.isPartOfSet ? formData.questionOrder : null,
      };

      await axios.post(`${API_URL}/api/teacher/questions`, questionData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess('¡Pregunta creada exitosamente!');
      
      // Limpiar formulario
      setTimeout(() => {
        setFormData({
          area: formData.area, // Mantener el área seleccionada
          topic: '',
          competency: '',
          component: '',
          question: '',
          options: ['', '', '', ''],
          correct_answer: '',
          explanation: '',
          difficulty: 'medium',
          hasContextText: false,
          contextText: '',
          hasContextImage: false,
          contextImage: '',
          hasQuestionImage: false,
          questionImage: '',
          isPartOfSet: formData.isPartOfSet, // Mantener si es parte de un set
          questionSetId: formData.questionSetId, // Mantener el set ID
          questionOrder: formData.isPartOfSet ? formData.questionOrder + 1 : 1, // Incrementar orden
        });
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al crear la pregunta');
    } finally {
      setLoading(false);
    }
  };

  const getTopicsForArea = () => {
    if (!structure || !formData.area) return [];
    const areaData = structure.areas[formData.area];
    
    // Para ciencias, devolver topics de todos los componentes
    if (formData.area === 'ciencias' && areaData.components) {
      const allTopics = [];
      areaData.components.forEach(comp => {
        if (comp.topics) {
          comp.topics.forEach(topic => {
            allTopics.push({
              ...topic,
              componentId: comp.id,
              componentName: comp.name,
            });
          });
        }
      });
      return allTopics;
    }
    
    return areaData?.topics || [];
  };

  const getCompetencies = () => {
    if (!structure || !formData.area) return [];
    return structure.areas[formData.area]?.competencies || [];
  };

  const getComponents = () => {
    if (!structure || formData.area !== 'ciencias') return [];
    return structure.areas.ciencias?.components || [];
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4" gutterBottom>
          Crear Nueva Pregunta
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crea preguntas organizadas según la estructura del examen Saber 11°
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Área y Clasificación */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
            1. Clasificación
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Fila 1: Área y Dificultad */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormSelect
                    label="Área"
                    value={formData.area}
                    onChange={handleAreaChange}
                    required
                    placeholder="Selecciona un área"
                    options={[
                      { value: 'matematicas', label: 'Matemáticas' },
                      { value: 'lectura-critica', label: 'Lectura Crítica' },
                      { value: 'sociales', label: 'Sociales y Ciudadanas' },
                      { value: 'ciencias', label: 'Ciencias Naturales' },
                      { value: 'ingles', label: 'Inglés' }
                    ]}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormSelect
                    label="Dificultad"
                    value={formData.difficulty}
                    onChange={(e) => handleChange('difficulty', e.target.value)}
                    placeholder="Selecciona dificultad"
                    options={[
                      { value: 'easy', label: 'Fácil' },
                      { value: 'medium', label: 'Media' },
                      { value: 'hard', label: 'Difícil' }
                    ]}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Fila 2: Tema y Competencia */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth disabled={!formData.area}>
                    <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>Tema</InputLabel>
                    <Select
                      value={formData.topic || ''}
                      onChange={(e) => handleChange('topic', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>{formData.area ? 'Ninguno (opcional)' : 'Selecciona un área primero'}</em>
                      </MenuItem>
                      {formData.area && getTopicsForArea().map((topic) => (
                        <MenuItem key={topic.id} value={topic.id}>
                          {topic.name}
                          {topic.componentName && ` (${topic.componentName})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth disabled={!formData.area}>
                    <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>Competencia</InputLabel>
                    <Select
                      value={formData.competency || ''}
                      onChange={(e) => handleChange('competency', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>{formData.area ? 'Ninguna (opcional)' : 'Selecciona un área primero'}</em>
                      </MenuItem>
                      {formData.area && getCompetencies().map((comp) => (
                        <MenuItem key={comp.id} value={comp.id}>
                          {comp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>

            {/* Fila 3: Componente */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth disabled={formData.area !== 'ciencias'}>
                    <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>Componente</InputLabel>
                    <Select
                      value={formData.component || ''}
                      onChange={(e) => handleChange('component', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>{formData.area === 'ciencias' ? 'Ninguno (opcional)' : 'Solo para Ciencias'}</em>
                      </MenuItem>
                      {formData.area === 'ciencias' && getComponents().map((comp) => (
                        <MenuItem key={comp.id} value={comp.id}>
                          {comp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }} /> {/* Espacio vacío para mantener balance */}
              </Box>
            </Grid>
          </Grid>

          {/* Contexto Compartido */}
          <Typography variant="h6" gutterBottom sx={{ mt: 5, mb: 2 }}>
            2. Contexto (Opcional)
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasContextText}
                  onChange={(e) => handleChange('hasContextText', e.target.checked)}
                />
              }
              label="Esta pregunta tiene un texto/enunciado compartido con otras preguntas"
            />
          </FormGroup>

          {formData.hasContextText && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Texto del contexto"
              value={formData.contextText}
              onChange={(e) => handleChange('contextText', e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Ej: Un fragmento literario, un caso de estudio, datos estadísticos..."
            />
          )}

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasContextImage}
                  onChange={(e) => handleChange('hasContextImage', e.target.checked)}
                />
              }
              label="Incluir imagen/gráfica compartida"
            />
          </FormGroup>

          {formData.hasContextImage && (
            <TextField
              fullWidth
              label="URL de la imagen del contexto"
              value={formData.contextImage}
              onChange={(e) => handleChange('contextImage', e.target.value)}
              sx={{ mt: 2 }}
              placeholder="https://ejemplo.com/imagen.jpg"
              InputProps={{
                startAdornment: <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          )}

          {/* Set de Preguntas */}
          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPartOfSet}
                  onChange={(e) => handleChange('isPartOfSet', e.target.checked)}
                />
              }
              label="Esta pregunta es parte de un conjunto de preguntas relacionadas"
            />
          </FormGroup>

          {formData.isPartOfSet && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="ID del conjunto de preguntas"
                  value={formData.questionSetId}
                  onChange={(e) => handleChange('questionSetId', e.target.value)}
                  placeholder="Ej: mat-est-001"
                  helperText="Use el mismo ID para agrupar preguntas relacionadas"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Orden"
                  value={formData.questionOrder}
                  onChange={(e) => handleChange('questionOrder', parseInt(e.target.value))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          )}

          {/* Pregunta */}
          <Typography variant="h6" gutterBottom sx={{ mt: 5, mb: 2 }}>
            3. Pregunta
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            required
            multiline
            rows={6}
            label="Texto de la pregunta"
            value={formData.question}
            onChange={(e) => handleChange('question', e.target.value)}
          />

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasQuestionImage}
                  onChange={(e) => handleChange('hasQuestionImage', e.target.checked)}
                />
              }
              label="Incluir imagen específica para esta pregunta"
            />
          </FormGroup>

          {formData.hasQuestionImage && (
            <TextField
              fullWidth
              label="URL de la imagen de la pregunta"
              value={formData.questionImage}
              onChange={(e) => handleChange('questionImage', e.target.value)}
              sx={{ mt: 2 }}
              placeholder="https://ejemplo.com/pregunta-imagen.jpg"
              InputProps={{
                startAdornment: <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          )}

          {/* Opciones */}
          <Typography variant="h6" gutterBottom sx={{ mt: 5, mb: 2 }}>
            4. Opciones de Respuesta
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <RadioGroup
            value={formData.correct_answer}
            onChange={(e) => handleChange('correct_answer', e.target.value)}
          >
            {formData.options.map((option, index) => (
              <Grid container spacing={1} alignItems="center" key={index} sx={{ mb: 2 }}>
                <Grid item>
                  <FormControlLabel
                    value={option}
                    control={<Radio />}
                    label={String.fromCharCode(65 + index)}
                    disabled={!option.trim()}
                    sx={{ mr: 1 }}
                  />
                </Grid>

                <Grid item xs>
                  <TextField
                    fullWidth
                    label={`Opción ${index + 1}`}
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    required
                  />
                </Grid>

                <Grid item>
                  {formData.options.length > 2 && (
                    <IconButton onClick={() => removeOption(index)} color="error" aria-label={`Eliminar opcion ${index + 1}`}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}
          </RadioGroup>

          {formData.options.length < 6 && (
            <Button
              startIcon={<AddIcon />}
              onClick={addOption}
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
            >
              Agregar Opción
            </Button>
          )}

          {/* Explicación */}
          <Typography variant="h6" gutterBottom sx={{ mt: 5, mb: 2 }}>
            5. Explicación
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Explicación de la respuesta correcta"
            value={formData.explanation}
            onChange={(e) => handleChange('explanation', e.target.value)}
            placeholder="Explica por qué la respuesta es correcta..."
          />

          {/* Botones */}
          <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Pregunta'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateQuestion;
